/**
 * Typed wrapper for service-financial-services bank-transfer endpoints
 * (Paystack bank-rail).
 *
 * Wired endpoints:
 *   - GET  /v1/bank/list?country=nigeria       (public — no auth)
 *   - GET  /v1/bank/resolve?accountNumber=&bankCode=
 *   - POST /v1/transfers/bank
 *   - GET  /v1/transfers/bank/me?status=&page=&pageSize=
 *
 * Mirrors `apps/service-financial-services/src/modules/bank-transfer/
 * bank-transfer.dto.ts`.
 */
import { requestJson, type RequestJsonOptions } from '../api-client';
import { getCustomerToken } from '../auth/tokenStore';
import { env } from '../env';

type CallOpts = Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> & {
  readonly bearerToken?: string;
};

function bearerOf(opts: CallOpts): string | undefined {
  return opts.bearerToken ?? getCustomerToken() ?? undefined;
}

// ---------------------------------------------------------------------------
// GET /v1/bank/list
// ---------------------------------------------------------------------------

export interface BankListItem {
  readonly name: string;
  readonly code: string;
  readonly longCode?: string;
  readonly country: string;
  readonly currency: string;
  readonly type: string;
}

export interface BankListResponse {
  readonly items: readonly BankListItem[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
}

export async function listBanks(
  country = 'nigeria',
  options: CallOpts = {},
): Promise<BankListResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<BankListResponse>({
    method: 'GET',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: `/v1/bank/list?country=${encodeURIComponent(country)}`,
    // Endpoint is public, but we still send the bearer if present so that
    // any downstream RBAC checks see a known principal.
    bearerToken: bearerOf(options),
    ...rest,
  });
}

// ---------------------------------------------------------------------------
// GET /v1/bank/resolve
// ---------------------------------------------------------------------------

export interface BankResolveResponse {
  readonly accountName: string;
  readonly accountNumber: string;
  readonly bankCode: string;
  readonly cached: boolean;
}

export async function resolveAccount(
  accountNumber: string,
  bankCode: string,
  options: CallOpts = {},
): Promise<BankResolveResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  const qs = new URLSearchParams({ accountNumber, bankCode }).toString();
  return requestJson<BankResolveResponse>({
    method: 'GET',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: `/v1/bank/resolve?${qs}`,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

// ---------------------------------------------------------------------------
// POST /v1/transfers/bank
// ---------------------------------------------------------------------------

export interface SubmitBankTransferRequest {
  readonly recipientAccountNumber: string;
  readonly recipientBankCode: string;
  readonly recipientName: string;
  /** Naira minor units (kobo) — integer. ₦1,000 → 100_000. */
  readonly amountMinor: number;
  readonly currency?: 'NGN';
  readonly narration?: string;
}

export interface SubmitBankTransferResponse {
  readonly id: string;
  readonly status: 'PENDING' | 'SUCCESS' | 'FAILED' | 'REVERSED';
  readonly reference: string;
  readonly recipientCode: string;
}

export async function submitBankTransfer(
  body: SubmitBankTransferRequest,
  options: CallOpts = {},
): Promise<SubmitBankTransferResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<SubmitBankTransferResponse>({
    method: 'POST',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/transfers/bank',
    body,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

// ---------------------------------------------------------------------------
// GET /v1/transfers/bank/me
// ---------------------------------------------------------------------------

export interface BankTransferItem {
  readonly id: string;
  readonly reference: string;
  readonly recipientAccountNumber: string;
  readonly recipientBankCode: string;
  readonly recipientName: string;
  readonly amountMinor: number;
  readonly currency: string;
  readonly status: 'PENDING' | 'SUCCESS' | 'FAILED' | 'REVERSED';
  readonly narration: string | null;
  readonly failureReason: string | null;
  readonly createdAt: string;
  readonly paidAt: string | null;
}

export interface ListMyBankTransfersResponse {
  readonly items: readonly BankTransferItem[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export async function listMyBankTransfers(
  query: { status?: string; page?: number; pageSize?: number } = {},
  options: CallOpts = {},
): Promise<ListMyBankTransfersResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  const qs = new URLSearchParams();
  if (query.status) qs.set('status', query.status);
  if (query.page !== undefined) qs.set('page', String(query.page));
  if (query.pageSize !== undefined) qs.set('pageSize', String(query.pageSize));
  const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : '';
  return requestJson<ListMyBankTransfersResponse>({
    method: 'GET',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: `/v1/transfers/bank/me${suffix}`,
    bearerToken: bearerOf(options),
    ...rest,
  });
}
